/**
 * Slice C.2 · DiaryAnswer.
 * ------------------------
 * Reads canonical `presentation.data` mirrored from the domain
 * authority. Depending on the utterance the specialist attaches
 * one of several diary shapes — this renderer surfaces only the
 * fields that are genuinely present and never fabricates any.
 *
 * Canonical fields (server → visible):
 *   • date_scope           → "Scope"           (raw string)
 *   • lesson_count         → "Lessons"         (integer)
 *   • current              → "Currently"       ({pupil_name,start_time,end_time,…})
 *   • next                 → "Next"            ({pupil_name,start_time,end_time,…})
 *   • filter               → "Filter"          (raw string, e.g. "morning")
 *   • spaces_available     → "Spaces free"     (integer)
 *   • state                → "State"           (raw string)
 *   • day_off              → shown as chip     (boolean)
 *   • next_available_time  → "Next available"  (HH:MM)
 *
 * Graceful compat (C.1 legacy fixture shape):
 *   • range + items are still rendered when the payload carries
 *     them, but neither is fabricated.
 */
function _lessonLine(l) {
  if (!l || typeof l !== "object") return null;
  const name  = typeof l.pupil_name === "string" ? l.pupil_name : null;
  const start = typeof l.start_time === "string" ? l.start_time : null;
  const end   = typeof l.end_time   === "string" ? l.end_time   : null;
  if (!name && !start) return null;
  const label = name || "Lesson";
  if (start && end) return `${label} · ${start}–${end}`;
  if (start)        return `${label} · ${start}`;
  return label;
}

export function DiaryAnswer({ presentation }) {
  const d = presentation.data || {};

  // Canonical fields (Slice C.2)
  const dateScope    = typeof d.date_scope === "string" ? d.date_scope : null;
  const lessonCount  = Number.isInteger(d.lesson_count)     ? d.lesson_count     : null;
  const filterLabel  = typeof d.filter === "string"         ? d.filter           : null;
  const spacesFree   = Number.isInteger(d.spaces_available) ? d.spaces_available : null;
  const state        = typeof d.state === "string"          ? d.state            : null;
  const dayOff       = d.day_off === true;
  const nextAvail    = typeof d.next_available_time === "string" ? d.next_available_time : null;
  const currentLine  = _lessonLine(d.current);
  const nextLine     = _lessonLine(d.next);

  // Legacy fixture compat (safe, only rendered if genuinely present)
  const range = typeof d.range === "string" ? d.range : null;
  const items = Array.isArray(d.items) ? d.items : null;

  const showDetails = (
    dateScope || lessonCount !== null || filterLabel
    || spacesFree !== null || state || dayOff
    || nextAvail || currentLine || nextLine
  );

  return (
    <div data-testid="ui-response-diary-answer" className="space-y-2">
      <p
        className="text-[14px] leading-relaxed text-zinc-100"
        data-testid="ui-diary-narration"
      >
        {presentation.narration}
      </p>
      {showDetails && (
        <dl className="text-[13px] grid grid-cols-2 gap-x-3 gap-y-1 text-zinc-300">
          {dateScope && (
            <>
              <dt className="text-zinc-500">Scope</dt>
              <dd data-testid="ui-diary-date-scope">{dateScope}</dd>
            </>
          )}
          {lessonCount !== null && (
            <>
              <dt className="text-zinc-500">Lessons</dt>
              <dd data-testid="ui-diary-lesson-count">{lessonCount}</dd>
            </>
          )}
          {currentLine && (
            <>
              <dt className="text-zinc-500">Currently</dt>
              <dd data-testid="ui-diary-current">{currentLine}</dd>
            </>
          )}
          {nextLine && (
            <>
              <dt className="text-zinc-500">Next</dt>
              <dd data-testid="ui-diary-next">{nextLine}</dd>
            </>
          )}
          {filterLabel && (
            <>
              <dt className="text-zinc-500">Filter</dt>
              <dd data-testid="ui-diary-filter">{filterLabel}</dd>
            </>
          )}
          {spacesFree !== null && (
            <>
              <dt className="text-zinc-500">Spaces free</dt>
              <dd data-testid="ui-diary-spaces-available">{spacesFree}</dd>
            </>
          )}
          {state && (
            <>
              <dt className="text-zinc-500">State</dt>
              <dd data-testid="ui-diary-state">{state}</dd>
            </>
          )}
          {dayOff && (
            <>
              <dt className="text-zinc-500">Day off</dt>
              <dd data-testid="ui-diary-day-off">yes</dd>
            </>
          )}
          {nextAvail && (
            <>
              <dt className="text-zinc-500">Next available</dt>
              <dd data-testid="ui-diary-next-available">{nextAvail}</dd>
            </>
          )}
        </dl>
      )}
      {range && (
        <p className="text-[12px] text-zinc-500" data-testid="ui-diary-range">{range}</p>
      )}
      {items && items.length > 0 && (
        <ul data-testid="ui-diary-items" className="text-[13px] text-zinc-200 space-y-1">
          {items.slice(0, 12).map((it, i) => {
            const when = typeof it?.when === "string" ? it.when : null;
            const who  = typeof it?.who  === "string" ? it.who  : null;
            const what = typeof it?.what === "string" ? it.what : null;
            return (
              <li key={i} className="flex gap-2">
                {when && <span className="text-zinc-500 shrink-0">{when}</span>}
                <span>{[who, what].filter(Boolean).join(" · ") || "—"}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default DiaryAnswer;
